<template>
  <div class="container-fluid py-4">
    <!-- En-tête avec gradient -->
    <!-- <div class="page-header mb-4">
      <div class="header-content">
        <div>
          <h2 class="header-title">Statistiques des signalements</h2>
          <p class="header-subtitle">Vue d'ensemble de l'activité de modération et des actions récentes</p>
        </div>
        <div class="header-badge">
          <i class="bi bi-shield-check me-2"></i>
          Système de modération
        </div>
      </div>
    </div> -->

    <!-- Cards de statistiques avec design riche -->
    <div class="row mb-4 g-4">
      <div class="col-md-3" v-for="(value, key) in statsCards" :key="key">
        <div class="stat-card" :class="value.cardClass">
          <div class="stat-card-inner">
            <div class="stat-icon-wrapper">
              <div class="stat-icon" :class="value.iconClass">
                <i :class="value.icon"></i>
              </div>
              <div class="stat-trend" v-if="value.trend">
                <i :class="value.trendIcon"></i>
              </div>
            </div>
            <div class="stat-content">
              <div class="stat-label">{{ value.label }}</div>
              <div class="stat-number">{{ value.count }}</div>
              <div class="stat-description">{{ value.description }}</div>
            </div>
          </div>
          <div class="stat-card-footer" :class="value.footerClass">
            <i :class="value.footerIcon" class="me-2"></i>
            {{ value.footerText }}
          </div>
        </div>
      </div>
    </div>

    <!-- Actions récentes avec design enrichi -->
    <div class="modern-card">
      <div class="card-header-modern">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <h4 class="card-title">
              <i class="bi bi-clock-history me-2"></i>
              Historique des actions
            </h4>
            <p class="card-subtitle">Toutes les actions de modération des 30 derniers jours</p>
          </div>
          <!-- <div class="header-actions">
            <button class="btn-filter">
              <i class="bi bi-funnel me-2"></i>
              Filtrer
            </button>
          </div> -->
        </div>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="modern-table">
            <thead>
              <tr>
                <th>
                  <i class="bi bi-calendar3 me-2"></i>
                  Date & Heure
                </th>
                <th>
                  <i class="bi bi-person me-2"></i>
                  Talent concerné
                </th>
                <th>
                  <i class="bi bi-lightning me-2"></i>
                  Action effectuée
                </th>
                <th>
                  <i class="bi bi-gear me-2"></i>
                  Type d'action
                </th>
                <th>
                  <i class="bi bi-info-circle me-2"></i>
                  Détails
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="action in actionsRecentes" :key="action.id">
                <td>
                  <div class="date-wrapper">
                    <div class="date-main">{{ formatDateMain(action.dateAction) }}</div>
                    <div class="date-time">{{ formatTime(action.dateAction) }}</div>
                  </div>
                </td>
                <td>
                  <div class="talent-wrapper">
                    <div class="talent-avatar">
                      {{ getInitials(action.talent) }}
                    </div>
                    <div class="talent-info">
                      <div class="talent-name">{{ action.talent.nom }} {{ action.talent.prenom }}</div>
                      <!-- <div class="talent-id">ID: #{{ action.talent.id }}</div> -->
                    </div>
                  </div>
                </td>
                <td>
                  <span 
                    class="action-badge"
                    :class="getBadgeClass(action.action)"
                  >
                    <i :class="getActionIcon(action.action)" class="me-1"></i>
                    {{ getActionLabel(action.action) }}
                  </span>
                </td>
                <td>
                  <span 
                    class="type-badge"
                    :class="action.estAutomatique ? 'type-auto' : 'type-manual'"
                  >
                    <i :class="action.estAutomatique ? 'bi bi-robot' : 'bi bi-person-gear'" class="me-1"></i>
                    {{ action.estAutomatique ? 'Automatique' : 'Manuel' }}
                  </span>
                </td>
                <td>
                  <div class="details-wrapper">
                    <div class="details-text">{{ getDetailsWithAdmin(action) }}</div>
                  </div>
                </td>
              </tr>

              <tr v-if="actionsRecentes.length === 0">
                <td colspan="5" class="empty-state">
                  <div class="empty-state-content">
                    <div class="empty-icon">
                      <i class="bi bi-inbox"></i>
                    </div>
                    <h5 class="empty-title">Aucune action récente</h5>
                    <p class="empty-text">Les actions de modération apparaîtront ici</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer-modern">
        <div class="footer-info">
          <i class="bi bi-info-circle me-2"></i>
          Affichage des {{ actionsRecentes.length }} dernières actions
        </div>
        <!-- <button class="btn-view-all">
          Voir tout l'historique
          <i class="bi bi-arrow-right ms-2"></i>
        </button> -->
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import ApiService from "@/services/ApiService";

export default defineComponent({
  name: "StatistiquesSignalements",
  setup() {
    const stats = ref({
      talentsSignales: 0,
      talentsDesactives: 0,
      totalSignalements: 0,
      totalReactivations: 0,
    });

    const actionsRecentes = ref<any[]>([]);

    const getStatistiques = async () => {
      try {
        const response = await ApiService.get("/statistiques/signalements");
        const data = response.data;
        stats.value.talentsSignales = data.statistiques.talentsSignales;
        stats.value.talentsDesactives = data.statistiques.talentsDesactives;
        stats.value.totalSignalements = data.statistiques.totalSignalements;
        stats.value.totalReactivations = data.statistiques.totalReactivations;
        actionsRecentes.value = data.actionsRecentes || [];
      } catch (error) {
        console.error("Erreur chargement stats:", error);
      }
    };

    const getBadgeClass = (action: string) => {
      const classes: { [key: string]: string } = {
        signalement: "badge-warning",
        desactivation: "badge-danger",
        reactivation: "badge-success",
        avis_defavorable_ajoute: "badge-danger",
        avis_defavorable_supprime: "badge-info",
      };
      return classes[action] || "badge-secondary";
    };

    const getActionIcon = (action: string) => {
      const icons: { [key: string]: string } = {
        signalement: "bi bi-exclamation-triangle-fill",
        desactivation: "bi bi-x-circle-fill",
        reactivation: "bi bi-check-circle-fill",
        avis_defavorable_ajoute: "bi bi-flag-fill",
        avis_defavorable_supprime: "bi bi-flag",
      };
      return icons[action] || "bi bi-circle";
    };

    const getActionLabel = (action: string) => {
      const labels: { [key: string]: string } = {
        signalement: "Signalement",
        desactivation: "Désactivation",
        reactivation: "Réactivation",
        avis_defavorable_ajoute: "Avis défavorable ajouté",
        avis_defavorable_supprime: "Avis défavorable supprimé",
      };
      return labels[action] || action;
    };

    const getDetailsWithAdmin = (action: any) => {
      if (!action.estAutomatique && action.admin) {
        const adminName = `${action.admin.nom} ${action.admin.prenom}`;
        
        if (action.action === 'desactivation') {
          return `Compte désactivé manuellement par ${adminName}`;
        } else if (action.action === 'reactivation') {
          return `Compte réactivé manuellement par ${adminName}`;
        }
        
        return action.commentaire 
          ? `${action.commentaire} (par ${adminName})` 
          : `Action effectuée par ${adminName}`;
      }
      
      return action.commentaire || 'Aucun commentaire';
    };

    const formatDateMain = (date: string) => {
      return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const formatTime = (date: string) => {
      return new Date(date).toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const getInitials = (talent: any) => {
      return `${talent.nom.charAt(0)}${talent.prenom.charAt(0)}`.toUpperCase();
    };

    const statsCards = computed(() => ({
      talentsSignales: { 
        label: "Talents signalés", 
        count: stats.value.talentsSignales,
        description: "En attente de vérification",
        icon: "bi bi-exclamation-triangle-fill",
        iconClass: "icon-warning",
        cardClass: "card-warning",
        footerClass: "footer-warning",
        footerIcon: "bi bi-eye",
        footerText: "Nécessite attention",
        trend: "up",
        trendIcon: "bi bi-arrow-up"
      },
      talentsDesactives: { 
        label: "Comptes désactivés", 
        count: stats.value.talentsDesactives,
        description: "Temporairement bloqué",
        icon: "bi bi-shield-x",
        iconClass: "icon-danger",
        cardClass: "card-danger",
        footerClass: "footer-danger",
        footerIcon: "bi bi-lock",
        footerText: "Compte suspendu",
        trend: "up",
        trendIcon: "bi bi-arrow-up"
      },
      totalSignalements: { 
        label: "Total signalements", 
        count: stats.value.totalSignalements,
        description: "Depuis le lancement",
        icon: "bi bi-flag-fill",
        iconClass: "icon-info",
        cardClass: "card-info",
        footerClass: "footer-info",
        footerIcon: "bi bi-bar-chart",
        footerText: "Cumul historique",
        trend: "up",
        trendIcon: "bi bi-arrow-up"
      },
      totalReactivations: { 
        label: "Réactivations", 
        count: stats.value.totalReactivations,
        description: "Comptes rétablis",
        icon: "bi bi-check-circle-fill",
        iconClass: "icon-success",
        cardClass: "card-success",
        footerClass: "footer-success",
        footerIcon: "bi bi-unlock",
        footerText: "Accès restauré",
        trend: "up",
        trendIcon: "bi bi-arrow-up"
      },
    }));

    onMounted(() => {
      getStatistiques();
    });

    return {
      stats,
      actionsRecentes,
      getBadgeClass,
      getActionLabel,
      getActionIcon,
      formatDateMain,
      formatTime,
      getInitials,
      statsCards,
      getDetailsWithAdmin,
    };
  },
});
</script>

<style scoped>
/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.header-subtitle {
  margin: 0.5rem 0 0 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

.header-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.625rem 1.25rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Stats Cards - Design Riche */
.stat-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.stat-card-inner {
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
}

.stat-icon-wrapper {
  position: relative;
}

.stat-icon {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  position: relative;
}

.icon-warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(251, 191, 36, 0.4);
}

.icon-danger {
  background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(248, 113, 113, 0.4);
}

.icon-info {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(96, 165, 250, 0.4);
}

.icon-success {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: white;
  box-shadow: 0 4px 14px rgba(52, 211, 153, 0.4);
}

.stat-trend {
  position: absolute;
  top: -8px;
  right: -8px;
  background: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10b981;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.stat-number {
  font-size: 2.25rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 0.375rem;
}

.stat-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  font-weight: 500;
}

.stat-card-footer {
  padding: 0.875rem 1.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-top: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
}

.footer-warning {
  background: #fffbeb;
  color: #b45309;
}

.footer-danger {
  background: #fef2f2;
  color: #b91c1c;
}

.footer-info {
  background: #eff6ff;
  color: #1e40af;
}

.footer-success {
  background: #ecfdf5;
  color: #047857;
}

/* Modern Card */
.modern-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-header-modern {
  padding: 1.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
}

.card-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0.375rem 0 0 0;
  font-weight: 400;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-filter {
  background: white;
  border: 1px solid #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.btn-filter:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

/* Modern Table */
.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead th {
  background: #f9fafb;
  padding: 1rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e5e7eb;
}

.modern-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;
}

.modern-table tbody tr:hover {
  background-color: #fafbfc;
}

.modern-table tbody tr:last-child {
  border-bottom: none;
}

.modern-table tbody td {
  padding: 1.25rem 1.5rem;
  font-size: 0.875rem;
  color: #374151;
  vertical-align: middle;
}

/* Date Wrapper */
.date-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-main {
  color: #111827;
  font-weight: 600;
  font-size: 0.875rem;
}

.date-time {
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Talent Wrapper */
.talent-wrapper {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.talent-avatar {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.talent-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.talent-name {
  color: #111827;
  font-weight: 600;
  font-size: 0.875rem;
}

.talent-id {
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Action Badges */
.action-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.025em;
  white-space: nowrap;
}

.badge-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #fde047;
}

.badge-danger {
  background: linear-gradient(135deg, #fecaca 0%, #fca5a5 100%);
  color: #991b1b;
  border: 1px solid #f87171;
}

.badge-success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.badge-info {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.badge-secondary {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  color: #374151;
  border: 1px solid #d1d5db;
}

/* Type Badge */
.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.type-auto {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

.type-manual {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

/* Details Wrapper */
.details-wrapper {
  max-width: 350px;
}

.details-text {
  color: #6b7280;
  font-size: 0.8125rem;
  line-height: 1.5;
}

/* Card Footer */
.card-footer-modern {
  padding: 1rem 1.75rem;
  background: #fafbfc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-info {
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.btn-view-all {
  background: transparent;
  border: none;
  color: #667eea;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.btn-view-all:hover {
  color: #764ba2;
  transform: translateX(4px);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 0 !important;
}

.empty-state-content {
  padding: 4rem 2rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0;
}
</style>